/**
 * Generic DataTable Component
 *
 * A reusable, shadcn-styled table built on @tanstack/react-table
 * with optional row virtualization via @tanstack/react-virtual.
 */

import * as React from "react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type Column,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronLeft, ChevronRight, Loader2, Settings2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";

// ---------- Sortable Header ----------
function SortableHeader<TData>({
  column,
  children,
  className,
}: {
  column: Column<TData, unknown>;
  children: React.ReactNode;
  className?: string;
}) {
  if (!column.getCanSort()) {
    return <TableHead className={className}>{children}</TableHead>;
  }

  return (
    <TableHead className={className}>
      <Button
        variant="ghost"
        className="h-8 px-2 hover:bg-transparent text-xs font-medium"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        {children}
        {column.getIsSorted() === "asc" ? (
          <ArrowUp className="ml-2 h-3 w-3" />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowDown className="ml-2 h-3 w-3" />
        ) : (
          <ArrowUpDown className="ml-2 h-3 w-3 text-muted-foreground" />
        )}
      </Button>
    </TableHead>
  );
}

// ---------- Column Toggle Dropdown ----------
function ColumnVisibilityToggle<TData>({ table }: { table: ReturnType<typeof useReactTable<TData>> }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0">
          <Settings2 className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {table
          .getAllColumns()
          .filter((col) => typeof col.accessorFn !== "undefined" && col.getCanHide())
          .map((col) => (
            <DropdownMenuCheckboxItem
              key={col.id}
              checked={col.getIsVisible()}
              onCheckedChange={(value) => col.toggleVisibility(!!value)}
              className="text-xs capitalize"
            >
              {typeof col.columnDef.header === "string" ? col.columnDef.header : col.id}
            </DropdownMenuCheckboxItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------- Props ----------
interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  /** Enable row virtualization for large lists */
  virtualize?: boolean;
  /** Estimated row height for virtualizer (default 45) */
  estimateSize?: number;
  /** Server-side sorting: disable client sort, expose manual sorting state */
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  /** Pagination props (cursor-based or offset-based) */
  pagination?: {
    hasNext: boolean;
    hasPrevious: boolean;
    onNextPage: () => void;
    onPreviousPage: () => void;
    loading?: boolean;
    /** Display text, e.g. "Menampilkan 10 transaksi" */
    info?: string;
  };
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Empty state message */
  emptyMessage?: string;
  /** Empty state icon */
  emptyIcon?: React.ReactNode;
  /** Callback when a row is clicked */
  onRowClick?: (row: TData) => void;
  /** Extra content in the header area (e.g., column toggle) */
  headerExtra?: React.ReactNode;
  /** Show column visibility toggle */
  showColumnToggle?: boolean;
  /** Initial column visibility */
  initialColumnVisibility?: VisibilityState;
  /** Wrapper class */
  className?: string;
}

// ---------- DataTable Component ----------
export function DataTable<TData>({
  columns,
  data,
  virtualize = false,
  estimateSize = 45,
  manualSorting = false,
  sorting: controlledSorting,
  onSortingChange: onControlledSortingChange,
  pagination,
  loading = false,
  error = null,
  emptyMessage = "Tidak ada data",
  emptyIcon,
  onRowClick,
  headerExtra,
  showColumnToggle = false,
  initialColumnVisibility = {},
  className,
}: DataTableProps<TData>) {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(initialColumnVisibility);

  const sorting = controlledSorting ?? internalSorting;
  const setSorting = onControlledSortingChange ?? setInternalSorting;

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    manualSorting,
  });

  const rows = table.getRowModel().rows;
  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: 10,
    enabled: virtualize,
  });

  const virtualItems = virtualize ? rowVirtualizer.getVirtualItems() : null;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Header extras: column toggle, custom actions */}
      {(showColumnToggle || headerExtra) && (
        <div className="flex items-center justify-end gap-2">
          {headerExtra}
          {showColumnToggle && <ColumnVisibilityToggle table={table} />}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Memuat data...</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {emptyIcon}
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <div
          ref={virtualize ? parentRef : undefined}
          className={cn("rounded-md border", virtualize && "overflow-auto max-h-[600px]")}
        >
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="max-w-[14%]">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {virtualize && virtualItems ? (
                <>
                  {virtualItems.length > 0 && (
                    <tr>
                      <td colSpan={columns.length} style={{ height: virtualItems[0].start, padding: 0, border: "none" }} />
                    </tr>
                  )}
                  {virtualItems.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    return (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                        className={onRowClick ? "cursor-pointer" : undefined}
                        onClick={() => onRowClick?.(row.original)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} className="whitespace-nowrap max-w-[14%]">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                  {virtualItems.length > 0 && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        style={{
                          height: rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0),
                          padding: 0,
                          border: "none",
                        }}
                      />
                    </tr>
                  )}
                </>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={onRowClick ? "cursor-pointer hover:bg-muted/50" : undefined}
                    onClick={() => onRowClick?.(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="whitespace-nowrap max-w-[14%]">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {pagination && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">{pagination.info}</div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.onPreviousPage}
              disabled={!pagination.hasPrevious || pagination.loading || loading}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Sebelumnya
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.onNextPage}
              disabled={!pagination.hasNext || pagination.loading || loading}
            >
              Selanjutnya
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export { SortableHeader };
export type { DataTableProps };
